import { AndroidLogo } from '@phosphor-icons/react/AndroidLogo';
import { AppleLogo } from '@phosphor-icons/react/AppleLogo';
import { ArrowCircleRight } from '@phosphor-icons/react/ArrowCircleRight';
import { ArrowDown } from '@phosphor-icons/react/ArrowDown';
import { ArrowSquareOut } from '@phosphor-icons/react/ArrowSquareOut';
import { ArrowsLeftRight } from '@phosphor-icons/react/ArrowsLeftRight';
import { BuildingOffice } from '@phosphor-icons/react/BuildingOffice';
import { CalendarBlank } from '@phosphor-icons/react/CalendarBlank';
import { CaretDoubleLeft } from '@phosphor-icons/react/CaretDoubleLeft';
import { CaretDoubleRight } from '@phosphor-icons/react/CaretDoubleRight';
import { CaretDown } from '@phosphor-icons/react/CaretDown';
import { CaretLeft } from '@phosphor-icons/react/CaretLeft';
import { CaretRight } from '@phosphor-icons/react/CaretRight';
import { Chats } from '@phosphor-icons/react/Chats';
import { Check } from '@phosphor-icons/react/Check';
import { CircleDashed } from '@phosphor-icons/react/CircleDashed';
import { ClockCountdown } from '@phosphor-icons/react/ClockCountdown';
import { ClockUser } from '@phosphor-icons/react/ClockUser';
import { Copy } from '@phosphor-icons/react/Copy';
import { CurrencyCircleDollar } from '@phosphor-icons/react/CurrencyCircleDollar';
import { Database } from '@phosphor-icons/react/Database';
import { Dot } from '@phosphor-icons/react/Dot';
import { DotsThreeOutline } from '@phosphor-icons/react/DotsThreeOutline';
import { DownloadSimple } from '@phosphor-icons/react/DownloadSimple';
import { Eye } from '@phosphor-icons/react/Eye';
import { FileDashed } from '@phosphor-icons/react/FileDashed';
import { FileText } from '@phosphor-icons/react/FileText';
import { Files } from '@phosphor-icons/react/Files';
import { Flag } from '@phosphor-icons/react/Flag';
import { Gear } from '@phosphor-icons/react/Gear';
import { GlobeSimple } from '@phosphor-icons/react/GlobeSimple';
import { Handshake } from '@phosphor-icons/react/Handshake';
import { Heart } from '@phosphor-icons/react/Heart';
import { House } from '@phosphor-icons/react/House';
import { Info } from '@phosphor-icons/react/Info';
import { InstagramLogo } from '@phosphor-icons/react/InstagramLogo';
import { List } from '@phosphor-icons/react/List';
import { ListPlus } from '@phosphor-icons/react/ListPlus';
import { Lock } from '@phosphor-icons/react/Lock';
import { MagnifyingGlass } from '@phosphor-icons/react/MagnifyingGlass';
import { MegaphoneSimple } from '@phosphor-icons/react/MegaphoneSimple';
import { Package } from '@phosphor-icons/react/Package';
import { Pause } from '@phosphor-icons/react/Pause';
import { PauseCircle } from '@phosphor-icons/react/PauseCircle';
import { PencilSimple } from '@phosphor-icons/react/PencilSimple';
import { PlayCircle } from '@phosphor-icons/react/PlayCircle';
import { Plus } from '@phosphor-icons/react/Plus';
import { PlusCircle } from '@phosphor-icons/react/PlusCircle';
import { Prohibit } from '@phosphor-icons/react/Prohibit';
import { Question } from '@phosphor-icons/react/Question';
import { SealWarning } from '@phosphor-icons/react/SealWarning';
import { SignOut } from '@phosphor-icons/react/SignOut';
import { Smiley } from '@phosphor-icons/react/Smiley';
import { SmileySad } from '@phosphor-icons/react/SmileySad';
import { Spinner } from '@phosphor-icons/react/Spinner';
import { SquaresFour } from '@phosphor-icons/react/SquaresFour';
import { Star } from '@phosphor-icons/react/Star';
import { TiktokLogo } from '@phosphor-icons/react/TiktokLogo';
import { Timer } from '@phosphor-icons/react/Timer';
import { Translate } from '@phosphor-icons/react/Translate';
import { Trash } from '@phosphor-icons/react/Trash';
import { TrendUp } from '@phosphor-icons/react/TrendUp';
import { User } from '@phosphor-icons/react/User';
import { UserCircle } from '@phosphor-icons/react/UserCircle';
import { UserFocus } from '@phosphor-icons/react/UserFocus';
import { UserPlus } from '@phosphor-icons/react/UserPlus';
import { Users } from '@phosphor-icons/react/Users';
import { Video } from '@phosphor-icons/react/Video';
import { Warning } from '@phosphor-icons/react/Warning';
import { WarningCircle } from '@phosphor-icons/react/WarningCircle';
import { X } from '@phosphor-icons/react/X';
import { XCircle } from '@phosphor-icons/react/XCircle';
import { YoutubeLogo } from '@phosphor-icons/react/YoutubeLogo';
import type { CampaignNetwork, DevicePlatform } from '@ramdam/schema';
import { cn } from '@ramdam/ui/lib/utils';

import { captureException } from '~/errors/tracking/utils';
import { safeAbsurd } from '~/utils/safeAbsurd';

import { iconNames, type IconName as RamdamIconName } from './icons/sprite-types';
import spriteHref from './icons/sprite.svg';

// cf. https://phosphoricons.com/
const phosphorIcons = {
  question: { component: Question, variant: 'bold' },
  ship_physical_asset: { component: Package, variant: 'bold' },
  calendar_blank: { component: CalendarBlank, variant: 'fill' },
  arrow_down: { component: ArrowDown, variant: 'bold' },
  arrow_right_filled_circle: { component: ArrowCircleRight, variant: 'fill' },
  arrow_square_out: { component: ArrowSquareOut, variant: 'regular' },
  arrows_left_right: { component: ArrowsLeftRight, variant: 'regular' },
  building_office: { component: BuildingOffice, variant: 'bold' },
  caret_down: { component: CaretDown, variant: 'bold' },
  caret_left: { component: CaretLeft, variant: 'bold' },
  caret_double_left: { component: CaretDoubleLeft, variant: 'bold' },
  caret_double_right: { component: CaretDoubleRight, variant: 'bold' },
  circle_dashed: { component: CircleDashed, variant: 'bold' },
  collaboration_flagged: { component: Flag, variant: 'fill' },
  collaboration_unflagged: { component: Flag, variant: 'regular' },
  copy: { component: Copy, variant: 'bold' },
  download: { component: DownloadSimple, variant: 'regular' },
  flagged: { component: Flag, variant: 'fill' },
  unflagged: { component: Flag, variant: 'bold' },
  creator: { component: UserFocus, variant: 'regular' },
  user_focus: { component: UserFocus, variant: 'bold' },
  caret_right: { component: CaretRight, variant: 'bold' },
  check: { component: Check, variant: 'bold' },
  chats: { component: Chats, variant: 'bold' },
  clock_countdown: { component: ClockCountdown, variant: 'bold' },
  clock_user: { component: ClockUser, variant: 'bold' },
  currency_circle_dollar: { component: CurrencyCircleDollar, variant: 'bold' },
  database: { component: Database, variant: 'bold' },
  dot: { component: Dot, variant: 'fill' },
  dots_three_horizontal: { component: DotsThreeOutline, variant: 'fill' },
  draft: { component: FileDashed, variant: 'fill' },
  drag: { component: List, variant: 'bold' },
  file_text: { component: FileText, variant: 'bold' },
  files_bold: { component: Files, variant: 'bold' },
  files_regular: { component: Files, variant: 'regular' },
  globe_simple: { component: GlobeSimple, variant: 'bold' },
  heart: { component: Heart, variant: 'fill' },
  house: { component: House, variant: 'bold' },
  handshake: { component: Handshake, variant: 'bold' },
  instagram: { component: InstagramLogo, variant: 'regular' },
  apple: { component: AppleLogo, variant: 'bold' },
  android: { component: AndroidLogo, variant: 'bold' },
  instagram_bold: { component: InstagramLogo, variant: 'bold' },
  info: { component: Info, variant: 'regular' },
  list_plus_bold: { component: ListPlus, variant: 'bold' },
  lock: { component: Lock, variant: 'bold' },
  megaphone_simple: { component: MegaphoneSimple, variant: 'bold' },
  magnifying_glass: { component: MagnifyingGlass, variant: 'bold' },
  pause_circle: { component: PauseCircle, variant: 'bold' },
  play_circle: { component: PlayCircle, variant: 'regular' },
  play_circle_bold: { component: PlayCircle, variant: 'bold' },
  plus_circle: { component: PlusCircle, variant: 'regular' },
  pause: { component: Pause, variant: 'fill' },
  plus: { component: Plus, variant: 'fill' },
  plus_circle_bold: { component: PlusCircle, variant: 'bold' },
  pencil_simple: { component: PencilSimple, variant: 'bold' },
  x_circle_fill: { component: XCircle, variant: 'fill' },
  seal_warning: { component: SealWarning, variant: 'fill' },
  settings: { component: Gear, variant: 'regular' },
  star_regular: { component: Star, variant: 'regular' },
  square_four: { component: SquaresFour, variant: 'bold' },
  star: { component: Star, variant: 'fill' },
  sign_out: { component: SignOut, variant: 'bold' },
  smiley: { component: Smiley, variant: 'bold' },
  smiley_sad: { component: SmileySad, variant: 'bold' },
  spinner: { component: Spinner, variant: 'bold' },
  tiktok: { component: TiktokLogo, variant: 'regular' },
  tiktok_bold: { component: TiktokLogo, variant: 'bold' },
  timer: { component: Timer, variant: 'regular' },
  translate: { component: Translate, variant: 'fill' },
  trash: { component: Trash, variant: 'bold' },
  trend_up: { component: TrendUp, variant: 'regular' },
  users: { component: Users, variant: 'bold' },
  user: { component: User, variant: 'bold' },
  user_plus: { component: UserPlus, variant: 'bold' },
  user_circle: { component: UserCircle, variant: 'bold' },
  video: { component: Video, variant: 'bold' },
  warning: { component: Warning, variant: 'fill' },
  warning_circle: { component: WarningCircle, variant: 'bold' },
  X: { component: X, variant: 'bold' },
  youtube: { component: YoutubeLogo, variant: 'regular' },
  youtube_bold: { component: YoutubeLogo, variant: 'bold' },
  eye: { component: Eye, variant: 'bold' },
  prohibit: { component: Prohibit, variant: 'bold' },
} as const;

type PhosphorIcon = keyof typeof phosphorIcons;
export type IconName = PhosphorIcon | RamdamIconName;
export type IconProps = React.ComponentPropsWithoutRef<'svg'> & {
  name: IconName;
  className?: string;
  size?: number;
};

export const Icon = ({ name, className, size, ...rest }: IconProps) => {
  if (isPhosphorIcon(name)) {
    const { component: PhosphorIcon, variant } = phosphorIcons[name as PhosphorIcon];
    return (
      <PhosphorIcon
        data-icon-type="phosphor"
        data-icon-name={name}
        role="figure"
        weight={variant}
        className={cn('align-middle', className)}
        size={size ?? 16}
        {...rest}
      />
    );
  } else if (isCustomIcon(name)) {
    // const iconSize = size === 20 ? 'text-[1.25rem]' : 'text-base';

    return (
      <svg
        data-icon-type="ramdam"
        fill="currentColor"
        width={size ?? '1em'}
        height={size ?? '1em'}
        className={cn('text-base', className)}
      >
        <use href={`${spriteHref}#${name}`} />
      </svg>
    );
  } else {
    captureException(new Error('unknown_icon'), { extra: { icon: name } });
    console.error(`Unknown icon: ${name}`);
    const { component: PhosphorIcon, variant } = phosphorIcons['question'];
    return (
      <PhosphorIcon
        data-icon-type="phosphor"
        data-icon-name={'question'}
        role="figure"
        weight={variant}
        className={cn('align-middle', className)}
        size={size ?? 16}
      />
    );
  }
};

function isPhosphorIcon(name: IconName): name is PhosphorIcon {
  return name in phosphorIcons;
}

function isCustomIcon(name: IconName): name is RamdamIconName {
  return iconNames.includes(name as RamdamIconName);
}

export function getSocialMediaIconName(provider: CampaignNetwork): IconName {
  switch (provider) {
    case 'tiktok':
      return 'tiktok_bold';
    case 'instagram':
      return 'instagram_bold';
    case 'youtube':
      return 'youtube_bold';
    default:
      return safeAbsurd(provider, 'question', 'invalid_social_media_provider');
  }
}

export function getDevicePlatformIconName(provider: DevicePlatform): IconName {
  switch (provider) {
    case 'ios':
      return 'apple';
    case 'android':
      return 'android';
    default:
      return safeAbsurd(provider, 'question', 'invalid_mobile_os_provider');
  }
}
